import UserContext from "@contexts/UserContext";
import { SocketContext } from "@contexts/SocketContext";
import IUiSocketSetup from "@models/IUiSocketSetup";
import { useContext, useEffect, useMemo } from "react";
import * as Yup from 'yup';
import SupplierContext from "@contexts/SupplierContext";
import VendorContext from "@contexts/VendorContext";
import EndpointsEnum from "@models/EndpointsEnum";
import IOrder from "@models/IOrder";
import OwButton from "@components/shared/OwButton/OwButton";
import { OwFormikField } from "@components/shared/OwFormikField/OwFormikField";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwSelectOption from "@components/shared/OwSelectOption/OwSelectOption";
import IAuthNetAcceptAuthAndCaptureCreditCardDTO from "@models/DTO/IAuthNetAcceptAuthAndCaptureCreditCardDTO";
import IAuthNetOnAuthAndCaptureCreditCardDTO from "@models/DTO/IAuthNetOnAuthAndCaptureCreditCardDTO";
import HelperService from "@services/HelperService";
import { Formik, Form } from "formik";
import { useAcceptJs, PaymentData, DispatchDataResponse, AuthNetEnvironment } from "react-acceptjs";
import { AuthorizeNetContext } from "@contexts/AuthorizeNetContext";

type Props = {
    orderModel: IOrder;
};
    

interface BasicCardInfo {
    cardNumber: string;
    cardCode: string;
    month: string;
    year: string;
    zip: string;
}

const cardSchema = Yup.object().shape({
    cardNumber: Yup.string()
        .required('Card number is required')
        .matches(/^\d{13,16}$/, 'Card number must be 13 to 16 digits'),
    cardCode: Yup.string()
        .required('CVV is required')
        .matches(/^\d{3,4}$/, 'CVV must be 3 or 4 digits'),
    month: Yup.string()
        .required('Month is required')
        .test('is-valid-month', 'Please select a valid month', value => {
            return value !== undefined && value !== '';
        }),
    year: Yup.string()
        .required('Year is required')
        .test('is-valid-year', 'Please select a valid year', value => {
            return value !== undefined && value !== '';
        }),
    zip: Yup.string()
        .matches(/^\d{5}$/, 'Zip must be 5 digits'),
});

export const OrderPaymentAuthorizeNetNewCard = (props: Props) => {
    const supplierModel = useContext(SupplierContext);
    const userModel = useContext(UserContext);
    const vendorModel = useContext(VendorContext);
    const socket = useContext(SocketContext)!;

    const {existingCard, setExistingCard, newPaymentDetails, setNewPaymentDetails, isLoading, setIsLoading, nextSlide, prevSlide} = useContext(AuthorizeNetContext);

    const authData = {
        apiLoginID: supplierModel.Supplier_Integration.authnet_login_id || "",
        clientKey: supplierModel.Supplier_Integration.authnet_public_client_key || "",
    }

    const environment: AuthNetEnvironment = process.env.NODE_ENV === "production" ? "PRODUCTION" : "SANDBOX";

    const {loading, error, dispatchData } = useAcceptJs({authData, environment });

    const initialValues: BasicCardInfo = {
        cardCode: "",
        cardNumber: "",
        month: "",
        year: "",
        zip: "",
    };

    const handleSubmit = async (values: BasicCardInfo, { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }) => {
        try {
            const paymentData: PaymentData = {
                cardData: {
                    cardNumber: values.cardNumber,
                    cardCode: values.cardCode,
                    month: values.month,
                    year: values.year.substring(2),
                }
            };

            if (values.zip) {
                paymentData.cardData.zip = values.zip;
            }

            // Response codes Appendix: https://developer.authorize.net/api/reference/features/acceptjs.html#Appendix_Error_Codes

            const details = await dispatchData(paymentData);
            console.log('Received response:', values, details, error, loading);
            setSubmitting(false);

            setNewPaymentDetails(details);

            nextSlide();
            // updateOrder(details);
            // props.confirmFn();
        } catch (error) {
            console.error('Payment error:', error);
            setSubmitting(false);
        }
    }

    const monthsOptionsArray = useMemo(() => HelperService.getMonthsNumberOptionsArray(), []);
    const yearsOptionsArray = useMemo(() => HelperService.getYearsOptionsArray(), []);

    return <div className={"OrderPaymentAuthorizeNetNewCard"}>
        <Formik 
                initialValues={initialValues} 
                validationSchema={cardSchema}
                onSubmit={handleSubmit}
            >
                {({values, handleChange, errors, touched, isSubmitting, isValid}) => ( 
                    <Form>
                        <div className="mb-3">
                            <OwFormikField 
                                label="card_number"
                                name="cardNumber"
                            />
                        </div>
                        <div className="mb-3">
                            <div className="col">
                                <OwFormikField 
                                    label="cvv"
                                    name="cardCode" 
                                    className="w-5r"
                                />
                            </div>
                        </div>
                        <div className="mb-3">
                            <OwLabel for="expirationDate" className="form-label" title="expiration_date"/>
                            <div className="row">
                                <div className="col-6">
                                    <OwSelectOption 
                                        name="month" 
                                        valueProp="value" 
                                        titleProp="label" 
                                        fn={handleChange} 
                                        options={monthsOptionsArray} 
                                        selectedOption={values.month}
                                        hasSelectedOptionProp={false}
                                    />
                                    {errors.month && touched.month && <div className="text-danger">{errors.month}</div>}
                                </div>
                                <div className="col-6">
                                    <OwSelectOption 
                                        name="year" 
                                        valueProp="value" 
                                        titleProp="label" 
                                        fn={handleChange} 
                                        options={yearsOptionsArray} 
                                        selectedOption={values.year}
                                        hasSelectedOptionProp={false}
                                    />
                                    {errors.year && touched.year && <div className="text-danger">{errors.year}</div>}
                                </div>
                            </div>
                        </div>

                        <div className="mb-3">
                            <OwFormikField
                                label="postal_code"
                                name="zip"
                                className="w-7r"
                            />
                        </div>

                        <div className="d-flex justify-content-end">
                            <OwButton 
                                title="next" 
                                type="submit" 
                                className="btn-primary" 
                                active={isSubmitting} 
                                disableOnActive={true}
                                disabled={!isValid || isSubmitting}
                            />
                        </div>

                        
                    </Form>
                )}
            </Formik>
    </div>;
};
